import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* ═══════════════════════════════════════════════════════════════════════════
   THE BILL, ON THE PAPER'S OWN GRID

   Thermal paper is not a page. It is a column of character cells — 48 across
   on 80mm, 32 on 58mm — and the print head can only put a character in a cell.
   So this canvas IS that grid: one cell on screen is one character on paper,
   and a field dragged to row 3, column 24 prints at row 3, column 24. Nothing
   here can promise a placement the printer cannot make, which is the whole
   reason it is a grid and not a free page.

   ── WHAT A FIELD KNOWS ──
   row, col and width, in cells. Width matters because alignment happens inside
   it: a field at column 24 with width 24 and align=right ends at the paper's
   right edge, which is how a shop gets "GSTIN left, FSSAI right" on one line
   without either being a table.

   ── THE ONES THE BILL SIZES, NOT THE SHOPKEEPER ──
   The item table is as long as the basket; the tax summary as long as the
   rates used. They take the full width and their own rows, and the canvas
   draws them at a nominal height with a note saying so. Pretending to know
   their height would be the one lie this screen must not tell.
   ═══════════════════════════════════════════════════════════════════════════ */

const CELL_W = 11;          // a character cell on screen
const CELL_H = 19;
const MIN_ROWS = 26;

/* Blocks whose height the bill decides. The server keeps the same list. */
const TALL = ["items", "totals", "gstSummary", "tenders", "upiQr", "logo", "address", "customer"];
const TALL_ROWS = { items: 5, totals: 4, gstSummary: 3, tenders: 2, upiQr: 3, logo: 3, address: 2, customer: 1 };

const S = {
  wrap:    { background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14, padding: 12, overflowX: "auto" },
  ruler:   { position: "relative", height: 16, marginLeft: 26, fontFamily: "var(--mly-font-mono)", fontSize: 9, color: "var(--mly-text-muted)" },
  rulerNo: { position: "absolute", top: 0, transform: "translateX(-50%)" },
  body:    { display: "flex", alignItems: "flex-start" },
  rows:    { width: 26, fontFamily: "var(--mly-font-mono)", fontSize: 9, color: "var(--mly-text-muted)", textAlign: "right", paddingRight: 6, userSelect: "none" },
  paper:   { position: "relative", background: "#fff", boxShadow: "inset 0 0 0 1px rgba(0,0,0,.12)", cursor: "crosshair" },
  item:    { position: "absolute", boxSizing: "border-box", border: "1px solid #9aa4b2", background: "rgba(255,255,255,.9)",
             fontFamily: "var(--mly-font-mono)", fontSize: 11, lineHeight: `${CELL_H - 2}px`, color: "#111",
             padding: "0 2px", cursor: "grab", overflow: "hidden", whiteSpace: "pre" },
  itemOn:  { border: "1.5px solid var(--mly-accent)", boxShadow: "0 0 0 2px var(--mly-accent-soft)", zIndex: 3 },
  itemOff: { opacity: 0.45, borderStyle: "dashed" },
  tall:    { background: "rgba(16,24,40,.04)", color: "#475467", display: "flex", alignItems: "center", justifyContent: "center" },
  kill:    { position: "absolute", top: -7, right: -7, width: 14, height: 14, lineHeight: "12px", textAlign: "center",
             borderRadius: 3, border: "1px solid #b42318", background: "#d92d20", color: "#fff", fontSize: 9, cursor: "pointer", zIndex: 4 },
  grip:    { position: "absolute", right: -3, top: 0, bottom: 0, width: 6, cursor: "ew-resize", zIndex: 4 },
  legend:  { marginTop: 8, fontSize: 11, color: "var(--mly-text-muted)", lineHeight: 1.5 },
};

export default function BillCanvas({ cols, blocks, spec, selected, onSelect, onChange, onRemove, onDropNew }) {
  const paperRef = useRef(null);
  const [drag, setDrag] = useState(null);      // { id, mode, startX, startY, row, col, width }

  const idOf = (b) => b.id || b.key;

  /* How far down the paper goes: the last row anything sits on, plus room to
     drop something new. */
  const rows = useMemo(() => {
    let last = 0;
    blocks.forEach((b) => {
      const r = (b.row ?? 0) + (TALL.includes(b.key) ? (TALL_ROWS[b.key] || 2) : 1);
      if (r > last) last = r;
    });
    return Math.max(MIN_ROWS, last + 3);
  }, [blocks]);

  const cellAt = useCallback((e) => {
    const box = paperRef.current?.getBoundingClientRect();
    if (!box) return { row: 0, col: 0 };
    return {
      row: Math.max(0, Math.floor((e.clientY - box.top) / CELL_H)),
      col: Math.max(0, Math.min(cols - 1, Math.floor((e.clientX - box.left) / CELL_W))),
    };
  }, [cols]);

  /* Dragging and resizing both live on the window, so the pointer may leave
     the box (and the paper) without the field being dropped in the wrong
     place or stuck to the cursor. */
  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      const dCol = Math.round((e.clientX - drag.startX) / CELL_W);
      const dRow = Math.round((e.clientY - drag.startY) / CELL_H);
      if (drag.mode === "move") {
        const col = Math.max(0, Math.min(cols - 1, drag.col + dCol));
        const row = Math.max(0, drag.row + dRow);
        onChange(drag.id, { row, col });
      } else {
        const width = Math.max(1, Math.min(cols - drag.col, drag.width + dCol));
        onChange(drag.id, { width });
      }
    };
    const up = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [drag, cols, onChange]);

  const start = (e, b, mode) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(idOf(b));
    setDrag({
      id: idOf(b), mode,
      startX: e.clientX, startY: e.clientY,
      row: b.row ?? 0, col: b.col ?? 0, width: b.width ?? (cols - (b.col ?? 0)),
    });
  };

  /* What the box says. Not the real value — the bill has not been rung up yet
     — but the field's name, so a shopkeeper can see what is where. */
  const caption = (b) => {
    const c = spec(b.key) || {};
    if (b.key === "text") return (b.text || "").trim() || "your own words";
    if (b.key === "separator") return "".padEnd(40, (b.text || "solid") === "dotted" ? "." : (b.text || "solid") === "double" ? "=" : "-");
    if (b.key === "spacer") return " ";
    return c.label || b.key;
  };

  return (
    <div style={S.wrap}>
      {/* the ruler, in character cells */}
      <div style={{ ...S.ruler, width: cols * CELL_W }}>
        {Array.from({ length: Math.floor(cols / 5) + 1 }, (_, i) => i * 5).map((n) => (
          <span key={n} style={{ ...S.rulerNo, left: n * CELL_W + CELL_W / 2 }}>{n}</span>
        ))}
      </div>

      <div style={S.body}>
        <div style={{ ...S.rows, height: rows * CELL_H }}>
          {Array.from({ length: rows }, (_, r) => (
            <div key={r} style={{ height: CELL_H, lineHeight: `${CELL_H}px` }}>{r % 5 === 0 ? r : ""}</div>
          ))}
        </div>

        <div
          ref={paperRef}
          style={{
            ...S.paper,
            width: cols * CELL_W,
            height: rows * CELL_H,
            backgroundImage:
              `repeating-linear-gradient(to right, rgba(16,24,40,.10) 0 1px, transparent 1px ${CELL_W}px),` +
              `repeating-linear-gradient(to bottom, rgba(16,24,40,.07) 0 1px, transparent 1px ${CELL_H}px)`,
          }}
          onClick={() => onSelect(null)}
          onDragOver={(e) => { if (onDropNew) e.preventDefault(); }}
          onDrop={(e) => {
            if (!onDropNew) return;
            e.preventDefault();
            const { row, col } = cellAt(e);
            onDropNew(row, col);
          }}
        >
          {blocks.map((b) => {
            const id = idOf(b);
            const tall = TALL.includes(b.key);
            const col = b.col ?? 0;
            const width = tall ? cols : Math.max(1, Math.min(b.width ?? (cols - col), cols - col));
            const height = (tall ? (TALL_ROWS[b.key] || 2) : 1) * CELL_H;
            const chosen = selected === id;
            return (
              <div
                key={id}
                onPointerDown={(e) => start(e, b, "move")}
                onClick={(e) => { e.stopPropagation(); onSelect(id); }}
                title={(spec(b.key) || {}).hint || ""}
                style={{
                  ...S.item,
                  ...(tall ? S.tall : null),
                  ...(b.on === false ? S.itemOff : null),
                  ...(chosen ? S.itemOn : null),
                  left: (tall ? 0 : col) * CELL_W,
                  top: (b.row ?? 0) * CELL_H,
                  width: width * CELL_W,
                  height,
                  textAlign: b.align === "right" ? "right" : b.align === "centre" ? "center" : "left",
                  fontWeight: b.bold ? 700 : 400,
                  fontSize: b.big ? 13 : 11,
                }}
              >
                {caption(b)}
                {chosen && (
                  <>
                    <span style={S.kill} title="Take this off the bill"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onRemove(id); }}>✕</span>
                    {!tall && <span style={S.grip} title="Drag to set how wide it is"
                      onPointerDown={(e) => start(e, b, "resize")} />}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={S.legend}>
        One cell is one character on the paper — {cols} across. Drag a field to move it,
        drag its right edge to set how wide it is, and use the bar below to line the text up
        inside that width. The shaded fields are as tall as the bill makes them.
      </div>
    </div>
  );
}
